/**
 * Display rules shared by the roster and one contributor's profile.
 *
 * Both screens show an approval rate, and they have to read the same way: a
 * contributor whose row says 83% and whose profile says 82% has a bug, not a
 * rounding preference.
 */
import type { Money, RosterRow } from "../types/api";

/**
 * An approval rate as a percentage, or an em dash when there isn't one.
 *
 * Nobody has ruled on their work yet, so there is no rate. Showing 0% would
 * read as a bad contributor rather than as an empty review queue.
 */
export function approvalLabel(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  return `${Math.round(rate * 100)}%`;
}

/**
 * Whether a per-response rate is worth putting on every row.
 *
 * Rates are set per country. While they are all the same, repeating the same
 * figure down the page says nothing; once they differ it is the first thing
 * somebody reading the roster needs to know.
 */
export function ratesDiffer(rows: Array<{ rate: Money }>): boolean {
  return new Set(rows.map((row) => row.rate.cents)).size > 1;
}

/** What a row says about when this person was last seen doing anything. */
export function lastSeen(row: Pick<RosterRow, "metrics" | "last_login_at">,
                         formatDate: (iso?: string | null) => string): string {
  if (row.metrics.last_recorded_at) {
    return `last recorded ${formatDate(row.metrics.last_recorded_at)}`;
  }
  if (row.last_login_at) return `signed in ${formatDate(row.last_login_at)}`;
  return "never recorded";
}
